import get from "lodash/get";
import {useFormContext} from "react-hook-form";
import {MultiValue, SingleValue} from "react-select";

import {SelectField, SelectFieldBaseProps} from "@/components/form/fields/SelectField/SelectField.tsx";
import {LocationSelectLimited} from "@/components/LocationSelect/LocationSelectLimited.tsx";
import {URL_MONITORING_SERVERS} from "@/lib/constants/links.ts";
import {ServiceField} from "@/lib/constants/service";
import {ServiceDefinition} from "@/lib/types/checks.ts";
import {MonitoringGroup, MonitoringGroupLevel, MonitoringGroupsArchMapperEntry} from "@/lib/types/monitoring.ts";

import {filterAllowedMonitoringGroupsForCheckType} from "../utils";

type LocationsFieldProps = SelectFieldBaseProps & {
  svc?: ServiceDefinition;
  monitoringGroupsArchMapper?: MonitoringGroupsArchMapperEntry[];
  monitoringGroups: MonitoringGroup[];
  limit_monitoring_group_level: MonitoringGroupLevel;
  limit_monitoring_groups?: number;
  isMulti?: boolean;
} & (
    | {
        svc: ServiceDefinition;
        monitoringGroupsArchMapper: MonitoringGroupsArchMapperEntry[];
      }
    | {
        svc?: undefined;
        monitoringGroupsArchMapper?: undefined;
      }
  );

export const LocationsField = ({
  svc,
  name,
  monitoringGroupsArchMapper,
  monitoringGroups,
  limit_monitoring_group_level,
  limit_monitoring_groups,
  label = "Locations",
  required = true,
  isMulti: isMultiProps,
  ...rest
}: LocationsFieldProps) => {
  const {
    watch,
    setValue,
    formState: {defaultValues},
  } = useFormContext();
  const value = watch(name);
  const defaultValue = get(defaultValues, name);
  let options = monitoringGroups;
  let isMulti = isMultiProps;

  if (svc) {
    const isSingleLocation = svc.force_monitoring_group_limit === 1;

    // uncomment and replace monitoring groups with available groups
    // when archmapper is removed. Next filter is important to keep in API
    // const groupTypes = [svc.force_monitoring_group_type || MonitoringGroupType.NORMAL];
    // if (svc.service_type == MonitoringServiceType.TRANSACTION) {
    //   groupTypes.push(MonitoringGroupType.TRANSACTION_EXT);
    // }
    // let availableLocations = monitoringGroups.filter((g) => groupTypes.includes(g.type) || g.is_private);

    options = filterAllowedMonitoringGroupsForCheckType({
      monitoringGroupsArchMapper: monitoringGroupsArchMapper,
      serviceDef: svc,
      monitoringServiceType: svc.service_type,
      mspVersion: watch(ServiceField.VERSION),
      monitoringGroups,
    });
    isMulti = !isSingleLocation;
  }

  return (
    <SelectField
      name={name}
      required={required}
      disabled={limit_monitoring_group_level === MonitoringGroupLevel.LEVEL_0}
      label={label}
      clearErrorsOnChange
      helpText={
        <>
          <a href={URL_MONITORING_SERVERS} target="_blank">
            View Probe Servers
          </a>
          <span className="ms-1">list for whitelisting IPs.</span>
        </>
      }
      {...rest}
      select={
        <LocationSelectLimited
          defaultValue={options.filter(
            (monitoringGroup) => Array.isArray(defaultValue) && defaultValue.includes(monitoringGroup.id)
          )}
          value={options.filter((monitoringGroup) => Array.isArray(value) && value.includes(monitoringGroup.id))}
          onChange={(data) => {
            if (!isMulti) {
              const value = data as SingleValue<MonitoringGroup>;
              setValue(name, [(value as SingleValue<MonitoringGroup>)?.id], {shouldDirty: true, shouldValidate: true});
            } else {
              const value = data as MultiValue<MonitoringGroup>;
              setValue(
                name,
                value.map((d) => d.id),
                {shouldDirty: true, shouldValidate: true}
              );
            }
          }}
          isMulti={isMulti}
          options={options}
          limitMonitoringGroupLevel={limit_monitoring_group_level || MonitoringGroupLevel.LEVEL_0}
          limitMonitoringGroups={limit_monitoring_groups || 0}
        />
      }
    />
  );
};
